import React, { ReactNode, useState, useEffect } from 'react';
import { Button, Input, Textarea, ToastMessage } from '@cokitchen/cokitchen-components';
import Modal from 'react-modal';
import { CloseOutlineIcon } from 'cokitchen-icons';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { ENUM_SEARCH_TYPE } from 'utils/enum';
import Tippy from '@tippyjs/react';

type DataType = {
  name: string;
  meal_category_id: string;
  description: string;
  horizontal: boolean;
};
type EditMealCategoryProps = {
  show: boolean;
  children?: ReactNode;
  onClose: (newData?: DataType) => void;
  mealCategory: DataType;
  refreshData?: () => void;
};

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '10px',
    backgroundColor: '#fff',
    border: 'none',
    padding: '0px',
    overflow: 'unset',
    maxWidth: '400px',
    width: '60%'
  },
  overlay: {
    background: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(3px)',
    zIndex: 10000
  }
};

const EditMealCategoryModal = ({
  show,
  onClose,
  mealCategory,
  refreshData
}: EditMealCategoryProps) => {
  const [formData, setFormData] = useState({} as DataType);
  const [initialData, setInitialData] = useState({} as DataType);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFormData(mealCategory);
    setInitialData(mealCategory);
  }, [mealCategory]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const { pathname } = useLocation();
  const isAddonCategory = () => {
    return pathname.includes(ENUM_SEARCH_TYPE.ADDON_CATEGORY);
  };
  const getUpdatedPayload = () => {
    const updatedFields: Partial<Record<keyof DataType, string | boolean>> = {};

    Object.entries(formData).forEach(([key, value]) => {
      if (value !== initialData[key as keyof DataType]) {
        updatedFields[key as keyof DataType] = value;
      }
    });

    return updatedFields;
  };

  const getAddonCategoryPayload = () => {
    return {
      meal_addon_category_id: formData.meal_category_id,
      ...getUpdatedPayload()
    };
  };

  const getMealCategoryPayload = () => {
    const payload = getUpdatedPayload();
    return {
      meal_category_id: formData.meal_category_id,
      ...payload
    };
  };
  const updateMealCategory = async () => {
    setLoading(true);
    try {
      const url = isAddonCategory() ? `marketing/meal-addon-category` : `meal-category`;
      const payload = isAddonCategory() ? getAddonCategoryPayload() : getMealCategoryPayload();
      const res = await apiService.put(url, { data: payload });
      if (res?.data) {
        refreshData?.();
        toast(<ToastMessage type='success' message={`${formData?.name} updated successfully`} />);
        onClose(formData);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    }
    setLoading(false);
  };
  return (
    <div>
      <Modal isOpen={show} style={customStyles} ariaHideApp={false}>
        <div className='pb-8 px-5 space-y-5 text-[#1A1A1A] bg-white rounded-[10px]'>
          <div className='flex justify-between items-center border-b border-solid border-[#EFF0F0] py-4 -mx-[1.2rem] '>
            <div></div>
            <h2 className='font-medium '>Edit {mealCategory.name}</h2>
            <div className='flex justify-center items-center rounded-full bg-[#EFF0F0] mr-3 w-5 h-5'>
              <CloseOutlineIcon className='cursor-pointer ' onClick={() => onClose()} />
            </div>
          </div>

          <Input
            label='Category Name'
            name='name'
            small
            inputClass='h-12 placeholder:text-sm'
            className='text-[14px] '
            placeholder='Rice dishes'
            value={formData.name}
            onChange={handleChange}
          />
          <Textarea
            label='Summary'
            name='description'
            className='h-[100px] text-[14px]'
            placeholder='Rice dishes'
            value={formData.description || ''}
            onChange={handleChange}
          />
          <Tippy
            placement='bottom-start'
            content={
              <p className='bg-white border border-gray-11 p-2 shadow-lg rounded'>
                If enabled, this category will appear as a horizontal scroll section on the mobile
                app.
              </p>
            }>
            <div
              onClick={() => setFormData({ ...formData, horizontal: !formData.horizontal })}
              className='flex items-center gap-2 cursor-pointer'>
              <Input
                type='checkbox'
                name='horizontal'
                checked={formData.horizontal}
                checkColor='gray'
              />
              <p className='text-sm font-medium'>Display Horizontally </p>
            </div>
          </Tippy>

          <div className='flex justify-center '>
            <Button
              onClick={updateMealCategory}
              loading={loading}
              className=' h-[40px] mt-9'
              color='primary'
              variant='block'>
              Update
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EditMealCategoryModal;
